import ScenarioTree, { DefaultSelection } from "@components/ScenarioTree"
import { GraphDto } from "@custom-types/graphs"
import { socketLeaveCrdtRoom, useSocketIO } from "@features/builder/hooks/socketIOHooks"
import { GraphBuilderPagePath } from "@features/builder/const"
import MainContent from "@features/builder/MainContent"
import TopActionBar from "@features/builder/TopActionBar/TopActionBar"
import {
  GraphDetails,
  GraphDocument,
  LinkedGraphConfig,
  SampleRequestDto,
} from "@features/builder/types"
import ErrorPage, { ErrorStatus } from "@features/errors/ErrorPage"
import { useHasAccess } from "@hooks/useHasAccess"
import { Button, CircularProgress, Grid } from "@mui/material"
import {
  baseGraphFamiliesApi,
  useGetFamilyBasesQuery,
} from "@store/services/baseGraphFamiliesService"
import { Edge, useReactFlow } from "@xyflow/react"
import { useEffect, useState } from "react"
import { useNavigate, useSearchParams } from "react-router"
import { getSortedGraphBases } from "utils/graph"
import UpdateMetadata from "@features/builder/UpdateMetadata"
import GraphDocumentProvider from "@features/builder/GraphDocumentProvider"
import { RunGroupFormValues } from "@features/run/RunGroupDialog/RunGroupForm"
import { useAppDispatch } from "@store/hooks"
import { productsApi } from "@store/services/productsService"
import ConfirmDialog from "@components/ConfirmDialog"
import ConfirmContractDialog from "@components/ConfirmContractDialog"
import {
  useDeleteGraphMutation,
  useExposeGraphMutation,
  useLazyGetGraphQuery,
} from "@store/services/graphsService"
import { ContractFamilyInfo } from "@store/authAtoms"
import usePermissions from "@hooks/usePermissions"
import DeleteDialog from "@components/DeleteDialog"
import logger from "@utils/logger"
import ValidationConfigurationDialog from "@features/builder/ValidationConfiguration/ValidationConfigurationDialog"
import { closeSnackbar, enqueueSnackbar } from "notistack"
import SnapshotSaveDialog from "@features/builder/Snapshot/SnapshotSaveDialog"
import { SnapshotRestoreDialog } from "@features/builder/Snapshot/SnapshotRestoreDialog"
import { GraphValidationDialog } from "@features/builder/GraphValidation/GraphValidationDialog"
import {
  getValidationHash,
  getValidationNodesFromDoc,
} from "@features/builder/GraphValidation/validationUitls"
import { graphValidationDataAtom } from "@features/builder/GraphValidation/graphValidationDataAtom"
import BuilderRunGroupDialog from "@features/builder/BuilderRunGroupDialog"
import { format } from "date-fns"
import {
  useEditorImportGraphMutation,
  useEditorSampleMutation,
  useLazyEditorExportGraphQuery,
} from "@store/services/editorService"
import { DashboardConfiguration } from "@features/builder/DashboardConfiguration/DashboardConfiguration"
import { useAtom, useSetAtom } from "jotai"
import { validateGraph } from "@features/builder/graph/validation/validateGraph"
import { AppNode, EdgeData } from "@features/builder/graph/types"
import { downloadFile } from "@utils/file"
import { presamplingValidationAtom } from "@features/builder/graph/nodes/validation/presamplingValidationAtom"
import BulkNodesEditDialog from "@features/builder/BulkNodesEditDialog/BulkNodesEditDialog"
import {
  allLinkedGraphValidationsErrorsAtom,
  getAllLinkedGraphValidationErrors,
} from "@features/run/RunGroupDialog/RunGroupForm/useAllLinkedGraphValidations"
import { getGraphLinks } from "@utils/node/getGraphLinks"
import { nodeFormHasUnsavedChangesAtom, useGraph } from "@features/builder/graph/graphAtoms"
import ValidationErrorsSnackbar from "@features/builder/graph/validation/ValidationErrorsSnackbar"
import ImportProgressDialog from "@features/builder/Import/ImportProgressDialog"

export enum PageMode {
  Start = "start",
  Edit = "edit",
}

const GraphBuilderPage = () => {
  const socket = useSocketIO()
  const [hasNodeFormUnsavedChanges] = useAtom(nodeFormHasUnsavedChangesAtom)
  const [graphValidationData, setGraphValidationData] = useAtom(graphValidationDataAtom)
  const [searchParams, setSearchParams] = useSearchParams({})
  const [familyKey, setFamilyKey] = useState("")
  const [defaultSelection, setDefaultSelection] = useState<DefaultSelection>()
  const [productIdToRefetch, setProductIdToRefetch] = useState("")
  const [isSaveSnapshotDialogOpen, setIsSaveSnapshotDialogOpen] = useState(false)
  const [isRestoreSnapshotDialogOpen, setIsRestoreSnapshotDialogOpen] = useState(false)
  const [graphDocument, setGraphDocument] = useState<GraphDocument | undefined>(undefined)
  const [isRunGroupDialogOpen, setIsRunGroupDialogOpen] = useState(false)
  const [postValidationCallback, setPostValidationCallback] = useState<() => void>()
  const [isDashboardDialogOpen, setIsDashboardDialogOpen] = useState(false)
  const [isValidationDialogOpen, setIsValidationDialogOpen] = useState(false)
  const [isPublishInternallyDialogOpen, setIsPublishInternallyDialogOpen] = useState(false)
  const [isBulkNodesEditDialogOpen, setIsBulkNodesEditDialogOpen] = useState(false)
  const [sanityHash, setSanityHash] = useState<string | undefined>(undefined)
  const [isExposingDialogOpen, setIsExposingDialogOpen] = useState<ContractFamilyInfo[]>([])
  const [postExposeGraph] = useExposeGraphMutation()
  const dispatch = useAppDispatch()
  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [deleteGraph] = useDeleteGraphMutation()
  const [editorSample, { isLoading: isSampling }] = useEditorSampleMutation()
  const [triggerGetGraph] = useLazyGetGraphQuery()
  const { permissions, refetch: refetchPermissions } = usePermissions()
  const navigate = useNavigate()
  const { isLocked } = useGraph()
  const isBlockSamplingWithWorkerValidationEnabled =
    import.meta.env.VITE_FF_BLOCK_SAMPLING_WITH_WORKER_VALIDATION !== "false"

  const [exportGraphQuery] = useLazyEditorExportGraphQuery()
  const [importGraph, { isLoading: isImportingGraph }] = useEditorImportGraphMutation()

  const setValidationResult = useSetAtom(presamplingValidationAtom)
  const setAllLinkedGraphValidationErrors = useSetAtom(allLinkedGraphValidationsErrorsAtom)

  const mode = (searchParams.get("mode") as PageMode | null) ?? PageMode.Start
  const isMetadataDialogOpen = searchParams.get("isMetadataDialogOpen") === "true"
  const isEditingMetadata = searchParams.get("metadataDialogMode") === "edit"
  const isGraphValidationDialogOpen = searchParams.get("graphValidation") === "true"

  const reactFlow = useReactFlow<AppNode, Edge<EdgeData>>()

  // sync familyKey with graphKey in search params
  useEffect(() => {
    const graphKeyParam = searchParams.get("graphKey")
    if (!graphKeyParam) {
      setFamilyKey("")
      return
    }
    const [organisationKey, divisionKey, productKey, familyKeyWithVersion] =
      decodeURIComponent(graphKeyParam).split("#")
    const [newFamilyKey] = (familyKeyWithVersion || "_").split("_")

    setFamilyKey(`${organisationKey}#${divisionKey}#${productKey}#${newFamilyKey}`)
  }, [searchParams])

  useEffect(() => {
    socket.connect()

    return () => {
      if (!socket.connected) {
        return
      }

      socket.disconnect()
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [])

  // on first render check url params so we can expand correct nodes in scenario tree
  // and pre-select correct base graph version in action bar (at the end resulting fetching correct base graph)
  useEffect(() => {
    const graphKeyParam = searchParams.get("graphKey")
    if (!graphKeyParam) {
      return
    }

    const [organisationKey, divisionKey, productKey, familyKeyWithVersion] =
      decodeURIComponent(graphKeyParam).split("#")

    const [newFamilyKey, version] = (familyKeyWithVersion || "_").split("_")
    setDefaultSelection({
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      organisationKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      divisionKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      productKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      familyKey: newFamilyKey,
      // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
      version,
    })

    return () => setDefaultSelection(undefined)
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [])

  const isPageAccessible = useHasAccess(GraphBuilderPagePath)
  const { data: graphBases = [], isFetching } = useGetFamilyBasesQuery(
    { familyKey: familyKey, roleHint: "build" },
    {
      skip: !isPageAccessible || !familyKey,
    },
  )
  const mostRecentGraph: GraphDto | undefined = familyKey
    ? getSortedGraphBases(graphBases)[0]
    : undefined

  useEffect(() => {
    if (!mostRecentGraph) {
      return
    }

    setGraphValidationData(undefined)
    setValidationResult(undefined)

    const isModeAlreadyMatching = searchParams.get("mode") === PageMode.Edit
    const graphKeyAlreadyMatching = searchParams.get("graphKey") === mostRecentGraph.key
    if (isModeAlreadyMatching && graphKeyAlreadyMatching) return

    setSearchParams((params) => {
      params.set("mode", PageMode.Edit)
      params.set("graphKey", mostRecentGraph.key)

      // clean up not related possible leftover query params
      params.delete("isMetadataDialogOpen")
      params.delete("metadataDialogMode")

      return params
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps -- FIX: deps after bulk migration
  }, [mostRecentGraph])

  if (!isPageAccessible) {
    return <ErrorPage status={ErrorStatus.Forbidden} />
  }

  // TODO kk: should be a decorator instead of higher order function, once it is supported - https://caniuse.com/decorators
  const protectActionFromNodeUnsavedChanges =
    <T extends (...args: Parameters<T>) => ReturnType<T>>(callback: T) =>
    (...args: Parameters<T>) => {
      if (hasNodeFormUnsavedChanges) {
        enqueueSnackbar("Before proceeding with this action, save or revert node form changes.", {
          persist: true,
          variant: "error",
        })
        return
      }

      callback(...args)
    }

  const handleFamilySelected = (newFamilyKey: string, _causedByDefaultSelection: boolean) => {
    setFamilyKey(newFamilyKey)
  }

  const openMetadataDialog = (isEditing: boolean) => {
    setSearchParams((params) => {
      params.set("isMetadataDialogOpen", true.toString())
      params.set("metadataDialogMode", isEditing ? "edit" : "create")

      return params
    })
  }

  const closeMetadataDialog = () => {
    setSearchParams((params) => {
      params.delete("isMetadataDialogOpen")
      params.delete("metadataDialogMode")

      return params
    })
  }

  const handleCreateGraph = () => {
    openMetadataDialog(false)
  }

  const handleProductRefetched = () => {
    // called after updating to stop this
    setProductIdToRefetch("")
  }

  const handleCreateNewGraph = (metadata: GraphDto | null) => {
    if (!metadata) return

    setSearchParams((params) => {
      params.delete("isMetadataDialogOpen")
      params.set("mode", PageMode.Edit)
      params.set("graphKey", metadata.key)

      return params
    })

    handleFamilySelected(metadata.base_graph_family_key, false)

    // INVALIDATE RTK
    // CHANGE SOME STATE ON SCENARIOTREE TO RE-RENDER
    dispatch(productsApi.util.invalidateTags(["ProductList"]))
    setDefaultSelection({
      organisationKey: metadata.product_organisation_key,
      divisionKey: metadata.product_division_key,
      productKey: metadata.product_key,
      familyKey: metadata.base_graph_family_key,
      version: metadata.version,
    })
    setProductIdToRefetch(
      `${metadata.product_organisation_key}#${metadata.product_division_key}#${metadata.product_key}`,
    )

    // creating new graph adds new permission related to that graph to the user
    refetchPermissions()
  }

  const validateCurrentGraph = async (
    validationInput:
      | { type: "publish_internally" }
      | { type: "sample"; graphDocument: GraphDocument },
  ) => {
    const graphDocumentToValidate =
      validationInput.type === "sample" ? validationInput.graphDocument : graphDocument

    if (!graphDocumentToValidate || !mostRecentGraph) {
      enqueueSnackbar("Graph not found", { variant: "error" })
      return
    }

    if (isBlockSamplingWithWorkerValidationEnabled && !validateGraph(reactFlow)) {
      return
    }

    try {
      const graphData = JSON.parse(
        graphDocumentToValidate?.getMap("graph").get("data")?.toJSON() ?? "null",
      ) as GraphDetails | null

      if (!graphData?.StartDate || !graphData?.EndDate) {
        enqueueSnackbar("Start date or end date is missing in graph data", { variant: "error" })
        return
      }

      if (!graphData?.Scenarios) {
        enqueueSnackbar("Scenarios are missing in graph data", { variant: "error" })
        return
      }

      const sha256 = await getValidationHash(graphDocumentToValidate, graphData.EndDate)
      const nodes = getValidationNodesFromDoc(graphDocumentToValidate)

      setGraphValidationData({
        doc: graphDocumentToValidate,
        startDate: graphData.StartDate,
        endDate: graphData.EndDate,
        scenarios: graphData.Scenarios,
        sha256,
        nodes,
      })
      setSearchParams((params) => {
        params.set("graphValidation", true.toString())

        return params
      })
    } catch {
      enqueueSnackbar("Parsing graph data failed", { variant: "error" })
    }
  }

  const handleMenuSampleClick = protectActionFromNodeUnsavedChanges((doc?: GraphDocument) => {
    if (!doc) return
    setGraphDocument(doc)
    setPostValidationCallback(() => () => setIsRunGroupDialogOpen(true))
    void validateCurrentGraph({ type: "sample", graphDocument: doc })
  })

  const handleValidationSuccess = (doc: GraphDocument, sanityHash: string) => {
    setSanityHash(sanityHash)
    postValidationCallback?.()
    handleGraphValidationDialogClose()
  }

  const handleSample = async (data: RunGroupFormValues) => {
    if (!mostRecentGraph?.key) return

    if (!sanityHash) {
      enqueueSnackbar("Sanity hash is missing, cannot proceed with sampling", { variant: "error" })
      logger.error("Sanity hash is missing, cannot proceed with sampling")
      return
    }

    if (data.linked_graph_mode === "MERGE" || data.linked_graph_mode === "PARALLEL") {
      const linkedGraphKeys = getGraphLinks(reactFlow.getNodes())
      const validationErrors = await getAllLinkedGraphValidationErrors(
        linkedGraphKeys,
        triggerGetGraph,
      )

      setAllLinkedGraphValidationErrors(validationErrors)

      if (validationErrors.length > 0) return
      else setAllLinkedGraphValidationErrors([])
    }

    let message: SampleRequestDto = {
      id: mostRecentGraph.key,
      run_name: data.run_name,
      num_samples: data.number_of_samplings,
      seed: data.seed,
      contract_key: data.contract_key || "private",
      description: data.description,
      scenarios: data.scenarios || [],
      end_date: data.end_date instanceof Date ? format(data.end_date, "yyyy-MM-dd") : data.end_date,
      validate: data.validate,
      sanity_graph_hash: sanityHash,
      sampling_engine: "EVIEWS",
    }

    if (data.linked_graph_mode === "PARALLEL") {
      message = {
        ...message,
        linked_graph_config: JSON.stringify({
          linked_graph_run_mode: "PARALLEL",
        }),
      }
    }

    if (data.linked_graph_mode === "PREVIOUS") {
      const linkedGraphPreviousRunGroups: LinkedGraphConfig["linked_graph_previous_run_groups"] = {}

      for (const prevRun of data.previous_run_key ?? []) {
        if (!prevRun?.graphKey) continue
        if (!prevRun?.runId) continue

        linkedGraphPreviousRunGroups[prevRun?.graphKey ?? ""] = { run_group_id: prevRun.runId }
      }

      message = {
        ...message,
        linked_graph_config: JSON.stringify({
          linked_graph_run_mode: "PREVIOUS",
          linked_graph_previous_run_groups: linkedGraphPreviousRunGroups,
        }),
      }
    }

    // ms: disconnect from socket so user who invoked sampling don't get double redirection
    // at this point we don't filter users when sending message about new version
    socket.disconnect()

    const msg = await editorSample(message).unwrap()
    const url = `/ScenarioDesign?mode=runs&graphKey=${encodeURIComponent(
      msg.graph_id,
    )}&runGroup=${encodeURIComponent(msg.run_group_id)}`
    dispatch(baseGraphFamiliesApi.util.invalidateTags(["BaseGraphFamilies"]))
    navigate(url)
  }

  const handleDeletePrivateGraph = protectActionFromNodeUnsavedChanges(() => {
    if (!mostRecentGraph) return

    setShowDeleteDialog(true)
  })

  const handleConfirmDeletePrivateGraph = () => {
    logger.debug("Deleting " + mostRecentGraph?.key)
    // TODO tr - we'd need to clean up the model in redis
    if (mostRecentGraph) {
      socketLeaveCrdtRoom(mostRecentGraph.key)
      socketLeaveCrdtRoom(mostRecentGraph.key)
      deleteGraph(mostRecentGraph.key).then(() => {
        setShowDeleteDialog(false)
        navigate("/Build")
        setProductIdToRefetch(
          `${mostRecentGraph.product_organisation_key}#${mostRecentGraph.product_division_key}#${mostRecentGraph.product_key}`,
        )
      })
    }
  }

  const handleExposeGraph = protectActionFromNodeUnsavedChanges(async () => {
    if (!mostRecentGraph) return

    const contractOptions = permissions?.build

    if (contractOptions && contractOptions.length == 1) {
      await postExposeGraph({
        key: mostRecentGraph.key,
        // @ts-expect-error Types needs fixing (value is possibly undefined) - temp bypass for bulk migration
        contractKey: contractOptions[0].contract_key,
      }).unwrap()
      dispatch(baseGraphFamiliesApi.util.invalidateTags(["BaseGraphFamilies"]))
    } else if (contractOptions && contractOptions.length > 1) {
      setIsExposingDialogOpen(contractOptions)
    }
  })

  const handleRunGroupDialogClose = () => {
    setIsRunGroupDialogOpen(false)
    setSanityHash(undefined)
    setPostValidationCallback(undefined)
  }

  const handlePublishInternallyClick = protectActionFromNodeUnsavedChanges(
    (doc?: GraphDocument) => {
      if (!doc || !mostRecentGraph) return

      setIsPublishInternallyDialogOpen(true)
      setGraphDocument(doc)
    },
  )

  const handlePublishInternallyConfirm = (isConfirmed: boolean) => {
    if (!mostRecentGraph?.key) return
    setIsPublishInternallyDialogOpen(false)

    if (!isConfirmed) {
      setPostValidationCallback(undefined)
      return
    }

    setPostValidationCallback(() => () => {
      socket.emit("req_publish_internally", { id: mostRecentGraph.key })

      // no longer needed as we are doing hard reload on "res_graph_new_version", leaving here in case we get rid of hard reload
      // socket.once("res_graph_new_version", () => {
      //   // invalidate graph bases cache as new graph version = new graph base
      //   dispatch(baseGraphFamiliesApi.util.invalidateTags(["BaseGraphFamilies"]))
      // })
    })

    void validateCurrentGraph({ type: "publish_internally" })
  }

  const handleExposeConfirm = async (cancel: boolean, value: string) => {
    if (!cancel && mostRecentGraph) {
      await postExposeGraph({
        key: mostRecentGraph.key,
        contractKey: value,
      }).unwrap()
      dispatch(baseGraphFamiliesApi.util.invalidateTags(["BaseGraphFamilies"]))
    }
    setIsExposingDialogOpen([])
  }

  const handleGraphValidationDialogClose = () => {
    setSearchParams((params) => {
      params.delete("graphValidation")

      return params
    })
  }

  const handleExportGraph = protectActionFromNodeUnsavedChanges(async () => {
    if (!mostRecentGraph?.key) return
    const resp = await exportGraphQuery(mostRecentGraph.key)
    if (!resp.data) return enqueueSnackbar("Problem with exporting graph", { variant: "error" })
    const blob = new Blob([resp.data])
    const blobUrl = URL.createObjectURL(blob)
    downloadFile(`${mostRecentGraph.name}.zip`, blobUrl)
  })

  const handleImportGraph = protectActionFromNodeUnsavedChanges(() => {
    if (!mostRecentGraph?.key) return

    const input = document.createElement("input")
    input.type = "file"
    input.accept = ".zip"

    input.onchange = async (e) => {
      const file = (e.target as HTMLInputElement).files?.[0]
      if (!file) return

      try {
        await importGraph({ graphKey: mostRecentGraph.key, graphZip: file }).unwrap()
        enqueueSnackbar("Graph imported successfully", { variant: "success" })
      } catch (e) {
        const errors = (e as { data: { errors?: string[] } }).data?.errors ?? [
          "Unknown error occurred during import",
        ]

        enqueueSnackbar("Graph import errors", {
          persist: true,
          anchorOrigin: { horizontal: "right", vertical: "top" },
          content: (key) => (
            <ValidationErrorsSnackbar id={key} errors={errors} title="Graph import failed" />
          ),
        })
      }
    }
    input.click()
  })

  const handleEditMetadataClick = protectActionFromNodeUnsavedChanges(() => {
    openMetadataDialog(true)
  })

  const handlePostSamplingValidationClick = protectActionFromNodeUnsavedChanges(() => {
    setIsValidationDialogOpen(true)
  })

  const handleDashboardClick = protectActionFromNodeUnsavedChanges(() => {
    setIsDashboardDialogOpen(true)
  })

  const handleRestoreSnapshotClick = protectActionFromNodeUnsavedChanges(() => {
    setIsRestoreSnapshotDialogOpen(true)
  })

  const handleSaveSnapshotClick = protectActionFromNodeUnsavedChanges(() => {
    setIsSaveSnapshotDialogOpen(true)
  })

  const handleNodesBulkEditClick = protectActionFromNodeUnsavedChanges(() => {
    if (isLocked) {
      enqueueSnackbar({
        message: "To open builk edit you need to unlock the graph",
        variant: "error",
        preventDuplicate: true,
        anchorOrigin: { horizontal: "right", vertical: "top" },
      })
    } else {
      setIsBulkNodesEditDialogOpen(true)
    }
  })

  return (
    <GraphDocumentProvider graphKey={mostRecentGraph?.key}>
      <Grid container sx={{ height: "calc(100vh - 64px)", pl: 0 }} wrap="nowrap" gap={1}>
        <DeleteDialog
          open={showDeleteDialog}
          setOpen={setShowDeleteDialog}
          onDeleteClick={handleConfirmDeletePrivateGraph}
        />
        {isSaveSnapshotDialogOpen && mostRecentGraph && (
          <SnapshotSaveDialog
            graphKey={mostRecentGraph.key}
            onClose={() => setIsSaveSnapshotDialogOpen(false)}
          />
        )}
        {isRestoreSnapshotDialogOpen && mostRecentGraph && (
          <SnapshotRestoreDialog
            graphKey={mostRecentGraph.key}
            onClose={() => setIsRestoreSnapshotDialogOpen(false)}
          />
        )}
        {isRunGroupDialogOpen && mostRecentGraph && (
          <BuilderRunGroupDialog
            graph={mostRecentGraph}
            onSubmit={handleSample}
            isSubmitting={isSampling}
            onClose={handleRunGroupDialogClose}
          />
        )}
        {mostRecentGraph && graphValidationData && (
          <GraphValidationDialog
            open={isGraphValidationDialogOpen}
            data={graphValidationData}
            graph={mostRecentGraph}
            onClose={handleGraphValidationDialogClose}
            onValidationSuccess={handleValidationSuccess}
          />
        )}
        {isExposingDialogOpen && (
          <ConfirmContractDialog
            options={isExposingDialogOpen}
            open={isExposingDialogOpen.length > 0}
            dialogTitle={`Which contract do you want to expose this to?`}
            onClose={handleExposeConfirm}
          />
        )}
        {isPublishInternallyDialogOpen && (
          <ConfirmDialog
            open={isPublishInternallyDialogOpen}
            dialogTitle={`Confirm request for graph publish.`}
            onClose={handlePublishInternallyConfirm}
          />
        )}
        {isBulkNodesEditDialogOpen && (
          <BulkNodesEditDialog onClose={() => setIsBulkNodesEditDialogOpen(false)} />
        )}
        <ImportProgressDialog open={isImportingGraph} />
        <Grid item xs="auto">
          <ScenarioTree
            selectedFamilyKey={familyKey}
            onProductFamilySelect={handleFamilySelected}
            defaultSelection={defaultSelection}
            productIdToRefetch={productIdToRefetch}
            onProductRefetched={handleProductRefetched}
            roleHint={"build"}
          >
            <Grid item sx={{ m: "5px", p: 0 }} alignSelf="center">
              <Button fullWidth color="error" onClick={handleCreateGraph}>
                Build new graph
              </Button>
            </Grid>
          </ScenarioTree>
        </Grid>
        {isFetching ? (
          <Grid container item direction="column" alignContent="center" justifyContent="center">
            <Grid item>
              <CircularProgress />
            </Grid>
          </Grid>
        ) : (
          <>
            <Grid container item direction="column" xs gap={1} wrap="nowrap">
              <Grid item>
                <TopActionBar
                  mode={mode}
                  graph={mostRecentGraph}
                  onSaveSnapshotClick={handleSaveSnapshotClick}
                  onRestoreSnapshotClick={handleRestoreSnapshotClick}
                  onAddDashboardConfigurationClick={handleDashboardClick}
                  onAddPostSamplingValidationClick={handlePostSamplingValidationClick}
                  onPublishInternallyClick={handlePublishInternallyClick}
                  onExposeClick={handleExposeGraph}
                  onDeleteClick={handleDeletePrivateGraph}
                  onSampleClick={handleMenuSampleClick}
                  onEditMetadataClick={handleEditMetadataClick}
                  onExportGraphClick={handleExportGraph}
                  onImportGraphClick={handleImportGraph}
                  onBulkNodesEditClick={handleNodesBulkEditClick}
                />
              </Grid>
              <Grid item xs>
                <MainContent
                  mode={mode}
                  startModeProps={{
                    onBuildClick: () => handleCreateGraph(),
                  }}
                  editModeProps={{
                    graph: mostRecentGraph,
                  }}
                />
                <UpdateMetadata
                  graph={isEditingMetadata ? mostRecentGraph : undefined}
                  open={isMetadataDialogOpen}
                  onGraphCreated={handleCreateNewGraph}
                  onClose={() => closeMetadataDialog()}
                />
              </Grid>
            </Grid>
            {mostRecentGraph?.key && isDashboardDialogOpen && (
              <DashboardConfiguration
                graphKey={mostRecentGraph?.key}
                onClose={() => setIsDashboardDialogOpen(false)}
              />
            )}
            {mostRecentGraph?.key && isValidationDialogOpen && (
              <ValidationConfigurationDialog
                graphKey={mostRecentGraph?.key}
                onClose={() => {
                  closeSnackbar()
                  setIsValidationDialogOpen(false)
                }}
              />
            )}
          </>
        )}
      </Grid>
    </GraphDocumentProvider>
  )
}

export default GraphBuilderPage
